import type React from "react";
import {useEffect} from "react";
import {HTMLUtils} from "../../utils/HTML/HTMLUtils";
import type {KeyboardListener} from "../../utils/interaction/key/KeyboardListener";

// https://stackoverflow.com/questions/63359138/react-closing-a-dropdown-when-click-outside
export function useClickOutside(
	refs: React.RefObject<any>[],
	onClickOutside: (event: MouseEvent) => void,
	starterEvent: "mousedown" | "mouseup" = "mousedown",
	queryStrings: string[] = [`.${HTMLUtils.popupBackdropV5Class}`, `#${HTMLUtils.modalContainerId}`],
	useCapture: boolean = false,
	enabled: boolean = true,
) {
	useEffect(() => {
		if (!enabled) {
			return;
		}

		/**
		 * Invoke Function onClick outside of element
		 */
		function handleClickOutside(event: MouseEvent) {
			// If the user clicks on a popupwindow, or a dropdown option, we don't want to close its parent element, despite its focus got lost
			const domElements = queryStrings.flatMap((qs) => Array.from(document.querySelectorAll<HTMLElement>(qs)));
			const allDomElements = [...domElements, ...refs.map((ref) => ref.current)].filter((el) => !!el) as HTMLElement[];
			const isClickOutSideOfElements = allDomElements.every((el) => !el.contains(event.target as HTMLElement));

			if (isClickOutSideOfElements) {
				onClickOutside(event);
			}
		}
		// Bind
		document.addEventListener(starterEvent, handleClickOutside, useCapture);
		return () => {
			// dispose
			document.removeEventListener(starterEvent, handleClickOutside, useCapture);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [...refs, ...queryStrings, onClickOutside, starterEvent, useCapture, enabled]);
}

export function useMouseLeave(
	elementRef: React.RefObject<HTMLElement | null>,
	onMouseLeave: (event: MouseEvent) => void,
	exceptions: HTMLElement[] = [],
) {
	useEffect(() => {
		function handleMouseMove(event: MouseEvent) {
			const target = event.target as HTMLElement;
			const element = elementRef.current;
			const isTargetPartOfExceptions = target && [element, ...exceptions].some((exception) => HTMLUtils.isDescendant(exception, target, true));

			if (!isTargetPartOfExceptions) {
				onMouseLeave(event);
			}
		}

		document.addEventListener("mousemove", handleMouseMove);

		return () => {
			document.removeEventListener("mousemove", handleMouseMove);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [elementRef, onMouseLeave, ...exceptions]);
}

type KeyboardCloseButtons = typeof KeyboardListener.KEY_ENTER | typeof KeyboardListener.KEY_ESCAPE;
type KeyboardEventType = "keydown" | "keyup";

interface IKeyboardPressOptions {
	readonly enabled?: boolean;
	readonly eventType?: KeyboardEventType;
	readonly onlyLatest?: boolean;
	readonly stopPropagation?: boolean;
}

interface IStackedKeyboardPressHandler {
	readonly id: number;
	readonly key: KeyboardCloseButtons;
	readonly eventType: KeyboardEventType;
	readonly onPress: (e?: KeyboardEvent) => void;
	readonly stopPropagation: boolean;
}

let nextKeyboardPressHandlerId = 0;
const stackedKeyboardPressHandlers: IStackedKeyboardPressHandler[] = [];
const stackedKeyboardPressListeners = new Map<KeyboardEventType, (event: KeyboardEvent) => void>();

function isKeyboardEventForKey(event: KeyboardEvent, key: KeyboardCloseButtons) {
	return event.key === key || event.code === key;
}

function suppressNextKeyUp(key: KeyboardCloseButtons) {
	let timeoutId: number;

	const cleanup = () => {
		window.clearTimeout(timeoutId);
		document.removeEventListener("keyup", stopKeyUp, true);
	};

	const stopKeyUp = (event: KeyboardEvent) => {
		if (isKeyboardEventForKey(event, key)) {
			event.preventDefault();
			event.stopPropagation();
			event.stopImmediatePropagation();
			cleanup();
		}
	};

	document.addEventListener("keyup", stopKeyUp, true);
	timeoutId = window.setTimeout(cleanup, 1000);
}

function ensureStackedKeyboardPressListener(eventType: KeyboardEventType) {
	if (stackedKeyboardPressListeners.has(eventType)) {
		return;
	}

	const listener = (event: KeyboardEvent) => {
		const handler = stackedKeyboardPressHandlers
			.toReversed()
			.find((stackedHandler) => stackedHandler.eventType === eventType && isKeyboardEventForKey(event, stackedHandler.key));

		if (!handler) {
			return;
		}

		if (handler.stopPropagation) {
			event.preventDefault();
			event.stopPropagation();
			event.stopImmediatePropagation();

			if (eventType === "keydown") {
				suppressNextKeyUp(handler.key);
			}
		}

		handler.onPress(event);
	};

	stackedKeyboardPressListeners.set(eventType, listener);
	document.addEventListener(eventType, listener);
}

function removeStackedKeyboardPressListenerIfUnused(eventType: KeyboardEventType) {
	if (stackedKeyboardPressHandlers.some((handler) => handler.eventType === eventType)) {
		return;
	}

	const listener = stackedKeyboardPressListeners.get(eventType);

	if (listener) {
		document.removeEventListener(eventType, listener);
		stackedKeyboardPressListeners.delete(eventType);
	}
}

export function useCloseOnKeyboardPress(
	onPress: (e?: KeyboardEvent) => void,
	key: KeyboardCloseButtons,
	deps: (boolean | React.RefObject<any>)[] = [],
	options: IKeyboardPressOptions = {},
) {
	const {enabled = true, eventType = "keyup", onlyLatest = false, stopPropagation = false} = options;

	useEffect(() => {
		if (!enabled) {
			return;
		}

		if (onlyLatest) {
			const handler: IStackedKeyboardPressHandler = {
				id: ++nextKeyboardPressHandlerId,
				key,
				eventType,
				onPress,
				stopPropagation,
			};

			stackedKeyboardPressHandlers.push(handler);
			ensureStackedKeyboardPressListener(eventType);

			return () => {
				const index = stackedKeyboardPressHandlers.findIndex((stackedHandler) => stackedHandler.id === handler.id);

				if (index !== -1) {
					stackedKeyboardPressHandlers.splice(index, 1);
				}

				removeStackedKeyboardPressListenerIfUnused(eventType);
			};
		}

		function onKeyboardPress(e: KeyboardEvent) {
			if (isKeyboardEventForKey(e, key)) {
				if (stopPropagation) {
					e.preventDefault();
					e.stopPropagation();
					e.stopImmediatePropagation();

					if (eventType === "keydown") {
						suppressNextKeyUp(key);
					}
				}

				onPress(e);
			}
		}

		document.addEventListener(eventType, onKeyboardPress);

		return () => {
			document.removeEventListener(eventType, onKeyboardPress);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [...deps, onPress, key, enabled, eventType, onlyLatest, stopPropagation]);
}
